import { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

// Remove trailing /api from the URL to get the base API server URL
const API_URL = import.meta.env.VITE_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000';

const ImageUpload = ({ value, onChange, label = "Product Image" }) => {
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Update preview when value changes
    useEffect(() => {
        if (value) {
            // If it's a relative path, prepend the backend URL
            if (value.startsWith('/uploads/')) {
                setPreview(`${API_URL}${value}`);
            } else if (value.startsWith('http')) {
                // Already a full URL
                setPreview(value);
            } else if (value.startsWith('data:')) {
                // Data URL
                setPreview(value);
            } else {
                // Assume it's a relative path
                setPreview(value);
            }
        } else {
            setPreview(null);
        }
    }, [value]);

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            alert('Image size should be less than 5MB');
            return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);

        // Upload to server
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await fetch(`${API_URL}/api/upload`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const data = await response.json();
            // Save the relative path to the database
            const imagePath = data.data.url || data.data.path;
            onChange(imagePath);
            // Update preview with full URL
            setPreview(`${API_URL}${imagePath}`);
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
            setPreview(null);
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        setPreview(null);
        onChange('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
                {label}
            </label>

            <div className="flex items-start space-x-4">
                {/* Preview */}
                {preview ? (
                    <div className="relative group">
                        <img
                            src={preview}
                            alt="Preview"
                            className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300"
                        />
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                        <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                )}

                {/* Upload Button */}
                <div className="flex-1">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    <button
                        type="button"
                        onClick={handleClick}
                        disabled={uploading}
                        className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
                    >
                        <Upload className="h-4 w-4" />
                        <span>{uploading ? 'Uploading...' : preview ? 'Change Image' : 'Upload Image'}</span>
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                        PNG, JPG, WebP up to 5MB
                    </p>
                    {preview && (
                        <p className="text-xs text-gray-600 mt-1">
                            Image uploaded successfully
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImageUpload;
